import { ButtonHTMLAttributes, ReactNode } from "react";
import { clsx } from "clsx";
import { Loader2 } from "lucide-react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  children: ReactNode;
}

// Edgy Cyberpunk Button Styles
const variantStyles = {
  primary:
    "bg-gradient-primary text-white font-black shadow-glow-pink hover:shadow-glow-pink hover:-translate-y-0.5 active:scale-98 uppercase tracking-wider",
  secondary:
    "bg-transparent border-2 border-accent-cyan text-accent-cyan font-extrabold hover:bg-accent-cyan/10 uppercase tracking-wider",
  danger:
    "bg-red-600 text-white font-black shadow-glow-subtle hover:bg-red-700 active:scale-98 uppercase tracking-wider",
  ghost:
    "bg-transparent text-text-secondary hover:text-text-primary hover:bg-bg-card-alt font-bold",
};

const sizeStyles = {
  sm: "text-sm px-6 h-12 rounded",
  md: "text-base px-8 h-14 rounded-md",
  lg: "text-lg px-8 h-[70px] rounded-md",
};

export const Button = ({
  variant = "primary",
  size = "md",
  isLoading = false,
  disabled,
  children,
  className,
  ...props
}: ButtonProps) => {
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center font-display",
        "transition-all duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <Loader2
          className="mr-2 h-4 w-4 animate-spin"
          data-testid="loading-spinner"
        />
      )}
      {children}
    </button>
  );
};
